import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BusinessTrip, TripStatus } from '@dxtech/shared';
import { api } from '../api/client.js';
import { useApp } from '../state/app.js';
import { Spinner } from '../ui/common.js';
import { IconPlus } from '../ui/icons.js';
import { mmdd } from '../lib/format.js';
import { TripModal } from '../modals/TripModal.js';
import { expenseApi } from '../api/expense.js';
import { ExpenseModal, won } from './Expense.js';

const STATUS_STYLE: Record<TripStatus, { bg: string; fg: string }> = {
  예정: { bg: '#DBE7FE', fg: '#1D4ED8' },
  진행: { bg: '#E0F2FE', fg: '#0369A1' },
  완료: { bg: '#DCFCE7', fg: '#15803D' },
  취소: { bg: '#F1F5F9', fg: '#64748B' },
};

export function Trip(): JSX.Element {
  const { flash, setScreen } = useApp();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [expenseFor, setExpenseFor] = useState<BusinessTrip | null>(null); // 출장 연결 새 경비 청구

  const mine = useQuery({ queryKey: ['trips', 'mine'], queryFn: () => api.tripsMine() });
  const items = mine.data?.items ?? [];
  // 출장별 연결된 경비(작성중 포함) 건수·합계
  const expenses = useQuery({ queryKey: ['expenses', 'mine'], queryFn: () => expenseApi.list('mine') });
  const tripExp = (id: string) => (expenses.data?.items ?? []).filter((e) => e.tripId === id);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['trips'] }); qc.invalidateQueries({ queryKey: ['attendance'] }); qc.invalidateQueries({ queryKey: ['board'] }); };

  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelTrip(id),
    onSuccess: () => { flash('출장이 취소되었습니다'); refresh(); },
    onError: (e) => flash(e instanceof Error ? e.message : '취소 실패'),
  });

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', animation: 'dxfade .3s ease' }}>
      <div className="dx-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px 13px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>내 출장 내역</div>
          <button className="dx-btn dx-btn-primary" style={{ marginLeft: 'auto', fontSize: 13, padding: '8px 15px' }} onClick={() => setOpen(true)}>
            <IconPlus />출장 등록
          </button>
        </div>
        {mine.isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
        ) : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>등록된 출장이 없습니다.</div>
        ) : items.map((t) => {
          const st = STATUS_STYLE[t.status] ?? STATUS_STYLE['예정'];
          return (
            <div key={t.id} className="dx-row">
              <div style={{ width: 120, flexShrink: 0, fontSize: 13, color: '#6B7280' }}>
                {mmdd(t.startDate)}{t.endDate !== t.startDate ? ` ~ ${mmdd(t.endDate)}` : ''}
              </div>
              <div style={{ width: 140, flexShrink: 0, fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.location}</div>
              <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.detail ?? ''}</div>
              {tripExp(t.id).length > 0 && (
                <button onClick={() => setScreen('expense')} title="경비지출에서 보기"
                  style={{ border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 12, color: '#2563EB', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  경비 {tripExp(t.id).length}건 · {won(tripExp(t.id).reduce((a, e) => a + e.total, 0))}
                </button>
              )}
              <span className="dx-chip" style={{ background: st.bg, color: st.fg }}>{t.status}</span>
              {t.status !== '취소' && (
                <button className="dx-btn dx-btn-soft dx-btn-sm" style={{ flexShrink: 0 }} onClick={() => setExpenseFor(t)}>경비 청구</button>
              )}
              {t.status !== '취소' && (
                <button className="dx-btn dx-btn-danger-outline dx-btn-sm" style={{ flexShrink: 0 }} onClick={() => cancel.mutate(t.id)} disabled={cancel.isPending}>취소</button>
              )}
            </div>
          );
        })}
      </div>

      {expenseFor && <ExpenseModal trip={expenseFor} onClose={() => setExpenseFor(null)} />}
      {open && <TripModal onClose={() => setOpen(false)} onDone={(m) => { flash(m); refresh(); }} />}
    </div>
  );
}
